import { logger } from "./logger";
/**
 * firmRules.js — Single canonical source of truth for firm rule definitions.
 *
 * ALL pages (comparison, firm profile, discount, rankings) MUST reference
 * this module for rule labels, descriptions, and validation.
 *
 * DO NOT define rule text or drawdown descriptions anywhere else.
 */

// ── Allowed drawdown types ──────────────────────────────────────────────
export const DRAWDOWN_TYPES = {
  EOD:              "EOD",
  TRAILING:         "Trailing",
  TRAILING_OR_EOD:  "Trailing or EOD",
  TRAILING_INTRADAY:"trailing_intraday",
  TRAILING_EOD:     "trailing_eod",
  BALANCE_BASED:    "Balance-based",
  STATIC:           "Static",
  HYBRID:           "Hybrid",
  FLEXIBLE:         "flexible",
  SCALING_STATIC:   "scaling_static",
};

// Map every known DB string → canonical key (case-insensitive lookup)
const DRAWDOWN_NORMALIZE = {
  // Trailing group
  "trailing":                               DRAWDOWN_TYPES.TRAILING,
  "live_trailing":                           DRAWDOWN_TYPES.TRAILING,
  "live trailing":                           DRAWDOWN_TYPES.TRAILING,
  "trailing drawdown":                       DRAWDOWN_TYPES.TRAILING,
  "live trailing (open & realized)":         DRAWDOWN_TYPES.TRAILING,
  "live trailing drawdown":                  DRAWDOWN_TYPES.TRAILING,
  "real-time trailing":                      DRAWDOWN_TYPES.TRAILING,
  "realtime trailing":                       DRAWDOWN_TYPES.TRAILING,
  // Trailing or EOD (multi-option)
  "trailing or eod":                         DRAWDOWN_TYPES.TRAILING_OR_EOD,
  "trailing/eod":                            DRAWDOWN_TYPES.TRAILING_OR_EOD,
  "trailing (option 1) / eod with scaling (option 2)": DRAWDOWN_TYPES.TRAILING_OR_EOD,
  // EOD group
  "eod":                                     DRAWDOWN_TYPES.EOD,
  "eod_dynamic":                             DRAWDOWN_TYPES.EOD,
  "eod dynamic":                             DRAWDOWN_TYPES.EOD,
  "end of day":                              DRAWDOWN_TYPES.EOD,
  "end-of-day":                              DRAWDOWN_TYPES.EOD,
  "eod drawdown":                            DRAWDOWN_TYPES.EOD,
  // EOD Trailing
  "eod trailing":                            DRAWDOWN_TYPES.TRAILING_EOD,
  "eod_trailing":                            DRAWDOWN_TYPES.TRAILING_EOD,
  "trailing_eod":                            DRAWDOWN_TYPES.TRAILING_EOD,
  "end-of-day trailing":                     DRAWDOWN_TYPES.TRAILING_EOD,
  "end of day trailing":                     DRAWDOWN_TYPES.TRAILING_EOD,
  "eod trailing drawdown":                   DRAWDOWN_TYPES.TRAILING_EOD,
  // Intraday Trailing
  "trailing_intraday":                       DRAWDOWN_TYPES.TRAILING_INTRADAY,
  "intraday trailing":                       DRAWDOWN_TYPES.TRAILING_INTRADAY,
  "intraday_trailing":                       DRAWDOWN_TYPES.TRAILING_INTRADAY,
  "intraday":                                DRAWDOWN_TYPES.TRAILING_INTRADAY,
  // Balance-based / Static
  "balance-based":                           DRAWDOWN_TYPES.BALANCE_BASED,
  "balance_based":                           DRAWDOWN_TYPES.BALANCE_BASED,
  "balance based":                           DRAWDOWN_TYPES.BALANCE_BASED,
  "static":                                  DRAWDOWN_TYPES.STATIC,
  "static drawdown":                         DRAWDOWN_TYPES.STATIC,
  // Hybrid / Flexible / Scaling
  "hybrid":                                  DRAWDOWN_TYPES.HYBRID,
  "flexible":                                DRAWDOWN_TYPES.FLEXIBLE,
  "scaling_static":                          DRAWDOWN_TYPES.SCALING_STATIC,
  "scaling static":                          DRAWDOWN_TYPES.SCALING_STATIC,
};

/**
 * Normalize a raw drawdown string to a canonical key.
 * NEVER returns raw DB values — always human-readable.
 */
export function normalizeDrawdownType(raw) {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  const match = DRAWDOWN_NORMALIZE[key];
  if (match) return match;
  // Unknown value — warn and return humanized version
  if (typeof console !== "undefined") logger.warn("Unknown drawdown_type:", raw);
  return raw;
}

/**
 * Humanize any unrecognized drawdown string for display.
 * Converts snake_case → Title Case, appends "Drawdown" if missing.
 */
function humanizeDrawdown(raw) {
  if (!raw) return "Unknown Drawdown";
  const humanized = raw
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
  if (humanized.toLowerCase().includes("drawdown")) return humanized;
  return `${humanized} Drawdown`;
}

// ── Locked rule descriptions (templates) ────────────────────────────────
export const DRAWDOWN_DESCRIPTIONS = {
  [DRAWDOWN_TYPES.EOD]:
    "End-of-Day (EOD) drawdown. Your trailing drawdown floor updates only at the close of each trading session — not during intraday swings. If your account peaks at session close, the floor adjusts overnight.",
  [DRAWDOWN_TYPES.TRAILING]:
    "Trailing drawdown. Your loss limit follows your equity high-water mark in real time during the session. If your account peaks at $53,000 with a $2,000 drawdown, your floor becomes $51,000 immediately.",
  [DRAWDOWN_TYPES.TRAILING_OR_EOD]:
    "Choose between Trailing or EOD drawdown at checkout. Trailing tracks your high-water mark in real time. EOD only updates your floor at session close. Pick the model that fits your trading style.",
  [DRAWDOWN_TYPES.TRAILING_INTRADAY]:
    "Intraday trailing drawdown. Your loss limit follows your equity high-water mark in real time during the session. Maximum flexibility for aggressive intraday strategies.",
  [DRAWDOWN_TYPES.TRAILING_EOD]:
    "End-of-Day trailing drawdown. Your drawdown floor updates only at the close of each trading session — intraday swings do not tighten your risk cushion.",
  [DRAWDOWN_TYPES.BALANCE_BASED]:
    "Balance-based (static) drawdown. Your drawdown floor is calculated from your starting balance and never moves up — regardless of how much profit you make. The most forgiving model for swing traders.",
  [DRAWDOWN_TYPES.STATIC]:
    "Static drawdown. Your loss floor is fixed at your starting balance minus the allowed drawdown. It never trails upward. Profits do not tighten your risk cushion.",
  [DRAWDOWN_TYPES.HYBRID]:
    "Hybrid drawdown model combining elements of trailing and static drawdown. Check the firm's specific rules for details.",
  [DRAWDOWN_TYPES.FLEXIBLE]:
    "Flexible drawdown with multiple options at checkout: Intraday Trailing (real-time tracking), EOD Trailing (session close updates), or Static (fixed floor). Choose the model that fits your strategy.",
  [DRAWDOWN_TYPES.SCALING_STATIC]:
    "Scaling static drawdown. Your loss floor is fixed but adjusts as you scale to larger account sizes. Check the firm's specific scaling rules.",
};

// Short labels for comparison tables
export const DRAWDOWN_LABELS = {
  [DRAWDOWN_TYPES.EOD]:              "EOD (End of Day)",
  [DRAWDOWN_TYPES.TRAILING]:         "Trailing (real-time)",
  [DRAWDOWN_TYPES.TRAILING_OR_EOD]:  "Trailing or EOD (choose at checkout)",
  [DRAWDOWN_TYPES.TRAILING_INTRADAY]:"Intraday Trailing Drawdown",
  [DRAWDOWN_TYPES.TRAILING_EOD]:     "End-of-Day Trailing Drawdown",
  [DRAWDOWN_TYPES.BALANCE_BASED]:    "Balance-Based Drawdown",
  [DRAWDOWN_TYPES.STATIC]:           "Static Drawdown",
  [DRAWDOWN_TYPES.HYBRID]:           "Hybrid Drawdown",
  [DRAWDOWN_TYPES.FLEXIBLE]:         "Flexible Drawdown (Intraday + EOD options)",
  [DRAWDOWN_TYPES.SCALING_STATIC]:   "Scaling Static Drawdown",
};

// Short labels for hub signals, compact UI, comparison cards
export const DRAWDOWN_LABELS_SHORT = {
  [DRAWDOWN_TYPES.EOD]:              "EOD",
  [DRAWDOWN_TYPES.TRAILING]:         "Trailing",
  [DRAWDOWN_TYPES.TRAILING_OR_EOD]:  "Trailing/EOD",
  [DRAWDOWN_TYPES.TRAILING_INTRADAY]:"Intraday Trailing",
  [DRAWDOWN_TYPES.TRAILING_EOD]:     "EOD Trailing",
  [DRAWDOWN_TYPES.BALANCE_BASED]:    "Balance-Based",
  [DRAWDOWN_TYPES.STATIC]:           "Static",
  [DRAWDOWN_TYPES.HYBRID]:           "Hybrid",
  [DRAWDOWN_TYPES.FLEXIBLE]:         "Flexible",
  [DRAWDOWN_TYPES.SCALING_STATIC]:   "Scaling Static",
};

export function getDrawdownLabel(firm) {
  if (!firm) return "Unknown Drawdown";
  const types = firm.drawdown_types;
  if (types && types.length > 0) {
    if (types.length === 1) {
      const norm = normalizeDrawdownType(types[0]);
      return DRAWDOWN_LABELS[norm] || humanizeDrawdown(types[0]);
    }
    const set = new Set(types);
    if (set.has("trailing_eod") && set.has("trailing_intraday")) return "Flexible Drawdown (Intraday + EOD options)";
    if (set.has("trailing_eod") && set.has("static")) return "EOD Trailing + Static Drawdown";
    return types.map(t => DRAWDOWN_LABELS[normalizeDrawdownType(t)] || humanizeDrawdown(t)).join(" + ");
  }
  const norm = normalizeDrawdownType(firm.drawdown_type);
  return DRAWDOWN_LABELS[norm] || humanizeDrawdown(firm.drawdown_type);
}

export function getDrawdownShort(firm) {
  if (!firm) return "Unknown";
  const types = firm.drawdown_types;
  if (types && types.length > 0) {
    if (types.length === 1) {
      const norm = normalizeDrawdownType(types[0]);
      return DRAWDOWN_LABELS_SHORT[norm] || humanizeDrawdown(types[0]);
    }
    const set = new Set(types);
    if (set.has("trailing_eod") && set.has("trailing_intraday")) return "Flexible Trailing";
    if (set.has("trailing_eod") && set.has("static")) return "EOD + Static";
    return types.map(t => DRAWDOWN_LABELS_SHORT[normalizeDrawdownType(t)] || humanizeDrawdown(t)).join(" + ");
  }
  const norm = normalizeDrawdownType(firm.drawdown_type);
  return DRAWDOWN_LABELS_SHORT[norm] || humanizeDrawdown(firm.drawdown_type);
}

export function hasDrawdownType(firm, keyword) {
  const kw = keyword.toLowerCase();
  const types = firm?.drawdown_types;
  if (types && types.length > 0) return types.some(t => t.toLowerCase().includes(kw));
  const legacy = firm?.drawdown_type;
  return legacy ? legacy.toLowerCase().includes(kw) : false;
}

export function getDrawdownDescription(firm) {
  if (!firm) return "";
  const norm = normalizeDrawdownType(firm.drawdown_type);
  return DRAWDOWN_DESCRIPTIONS[norm] || `${firm.drawdown_type} drawdown. Check the firm's website for detailed rules.`;
}

// ── Consistency rule formatting ─────────────────────────────────────────
export function formatConsistency(firm) {
  if (!firm) return "None";
  if (firm.consistency_pct) {
    const val = String(firm.consistency_pct).replace(/%/g, "").trim();
    if (val && val !== "null") return `${val}%`;
  }
  if (firm.consistency_rule) {
    const rule = firm.consistency_rule.toLowerCase();
    if (rule.includes("none") || rule.includes("no consistency")) return "None";
    if (rule.includes("smoothing")) return "Smoothing";
    const m = firm.consistency_rule.match(/(\d+)%/);
    if (m) return `${m[1]}%`;
    return firm.consistency_rule;
  }
  return "None";
}

// ── Required fields for validation ──────────────────────────────────────
const REQUIRED_FIELDS = [
  "slug", "name", "drawdown_type", "profit_split", "payout_frequency",
  "news_trading", "algos_allowed", "status",
];

const VALIDATED_DRAWDOWN_VALUES = new Set(Object.values(DRAWDOWN_TYPES));

/**
 * Validate a single firm object against the canonical schema.
 * Returns { valid: boolean, errors: string[] }
 */
export function validateFirm(firm) {
  const errors = [];
  if (!firm) return { valid: false, errors: ["Firm object is null/undefined"] };

  for (const field of REQUIRED_FIELDS) {
    if (firm[field] === undefined || firm[field] === null || firm[field] === "") {
      errors.push(`Missing required field: ${field}`);
    }
  }

  // Drawdown type validation
  if (firm.drawdown_type) {
    const norm = normalizeDrawdownType(firm.drawdown_type);
    if (!VALIDATED_DRAWDOWN_VALUES.has(norm)) {
      errors.push(`Invalid drawdown_type: "${firm.drawdown_type}" (normalized: "${norm}"). Allowed: ${[...VALIDATED_DRAWDOWN_VALUES].join(", ")}`);
    }
  }

  // Boolean field validation
  if (typeof firm.news_trading !== "boolean") {
    errors.push(`news_trading should be boolean, got: ${typeof firm.news_trading} (${firm.news_trading})`);
  }
  if (typeof firm.algos_allowed !== "boolean") {
    errors.push(`algos_allowed should be boolean, got: ${typeof firm.algos_allowed} (${firm.algos_allowed})`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Compare a STATIC_FIRMS entry against a DB firm record.
 * Returns { matches: boolean, mismatches: [{field, static, db}] }
 */
export function compareStaticVsDb(staticFirm, dbFirm) {
  const mismatches = [];
  if (!staticFirm || !dbFirm) return { matches: false, mismatches: [{ field: "object", static: !!staticFirm, db: !!dbFirm }] };

  const fields = [
    "drawdown_type", "profit_split", "payout_frequency", "first_payout_days",
    "news_trading", "algos_allowed", "activation_fee", "reset_fee",
    "min_days", "funded_min_days",
  ];

  for (const f of fields) {
    const sv = staticFirm[f];
    const dv = dbFirm[f];
    // Skip if DB has null/undefined
    if (dv === null || dv === undefined) continue;
    if (String(sv) !== String(dv)) {
      mismatches.push({ field: f, static: sv, db: dv });
    }
  }

  return { matches: mismatches.length === 0, mismatches };
}

/**
 * Scan text content for drawdown type mentions that conflict with firm's canonical type.
 * Returns array of { firm, expected, found, context }
 */
export function scanTextForDrawdownConflicts(text, firmSlug, firmDrawdownType) {
  const conflicts = [];
  if (!text || !firmSlug || !firmDrawdownType) return conflicts;

  const norm = normalizeDrawdownType(firmDrawdownType);
  const lowerText = text.toLowerCase();

  // Check for conflicting drawdown type mentions
  const patterns = {
    [DRAWDOWN_TYPES.EOD]: [/trailing.*drawdown/gi, /real[- ]time.*drawdown/gi],
    [DRAWDOWN_TYPES.TRAILING]: [/eod.*drawdown/gi, /end[- ]of[- ]day.*drawdown/gi, /static.*drawdown/gi],
    [DRAWDOWN_TYPES.BALANCE_BASED]: [/trailing.*drawdown/gi, /eod.*drawdown/gi],
    [DRAWDOWN_TYPES.STATIC]: [/trailing.*drawdown/gi, /eod.*drawdown/gi],
  };

  // Skip firms with multiple drawdown types (they legitimately mention all types)
  if (norm === DRAWDOWN_TYPES.TRAILING_OR_EOD || norm === DRAWDOWN_TYPES.TRAILING_INTRADAY) return conflicts;

  const conflictPatterns = patterns[norm] || [];
  for (const pattern of conflictPatterns) {
    const matches = lowerText.match(pattern);
    if (matches) {
      for (const match of matches) {
        // Find surrounding context
        const idx = lowerText.indexOf(match.toLowerCase());
        const context = text.substring(Math.max(0, idx - 30), Math.min(text.length, idx + match.length + 30));
        conflicts.push({
          firm: firmSlug,
          expected: norm,
          found: match,
          context: `...${context}...`,
        });
      }
    }
  }

  return conflicts;
}
